import React from "react";
import ReactGA from "react-ga";
import { connect, ConnectedProps } from "react-redux";
import { State } from "../store";
import { formatDistanceToNow, lightFormat } from "date-fns";
import { RouteComponentProps, withRouter } from "../polyrouter";
import { datadogLogs } from "@datadog/browser-logs";
import { LoggerOptions } from "../monitoring/LoggerOptions";

const mapStateToProps = (state: State) => ({
  recentOperations: state.graph.recentOperations
});
const mapDispatch = () => ({});
const connector = connect(mapStateToProps, mapDispatch);
type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = PropsFromRedux & {
  errorComponent: React.ReactNode;
  children: React.ReactNode;
};
type AppErrorBoundaryState = {
  hasError: boolean;
  error: Error | null;
};
type AppErrorBoundaryProps = RouteComponentProps & Props;

const logger = datadogLogs.createLogger("AppErrorBoundary", LoggerOptions);

export class AppErrorBoundaryRaw extends React.Component<
  Pick<Props, "errorComponent">,
  AppErrorBoundaryState
> {
  state = {
    hasError: false,
    error: null
  };
  componentDidCatch({ name, message }: Error): void {
    logger.error("Unhandled App Error", {
      name,
      message
    });
    ReactGA.exception({
      description: "Unhandled App Error",
      fatal: true
    });
  }
  render() {
    if (this.state.hasError) {
      return this.props.errorComponent;
    }
    return this.props.children;
  }
}

export class AppErrorBoundary extends React.Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  state = {
    hasError: false,
    error: null
  };

  static getDerivedStateFromError(error: any) {
    // Update state so the next render will show the fallback UI.
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(
    { name, message }: Error,
    errorInfo: React.ErrorInfo
  ): void {
    const { recentOperations } = this.props;
    const logOps = recentOperations.map(
      ({
        name,
        at,
        operation: {
          context: { meta }
        }
      }) => ({
        name,
        at: lightFormat(at, "HH:mm:ss"),
        age: formatDistanceToNow(at),
        cache: meta?.cacheOutcome
      })
    );
    logger.error("Unhandled App Error", {
      name,
      message,
      recentOperations: logOps
    });
    ReactGA.exception({
      description: "Unhandled App Error",
      fatal: true
    });
  }

  componentDidUpdate(prevProps: Readonly<RouteComponentProps>): void {
    const { location } = prevProps;
    const { location: nextLocation } = this.props;
    if (nextLocation !== location) {
      this.setState({
        hasError: false,
        error: null
      });
    }
  }

  render() {
    if (this.state.hasError) {
      return this.props.errorComponent;
    }
    return this.props.children;
  }
}

export default connector(
  withRouter<AppErrorBoundaryProps, typeof AppErrorBoundary>(AppErrorBoundary)
);
